import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import Spinner from './Spinner';
import MobileAppLoadingOverlay from './MobileAppLoadingOverlay';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';

const MOBILE_LOADING_PHASE_EVENT = 'flc:mobile-loading-phase';
const MOBILE_SPINNER_EVENT = 'flc:mobile-spinner-loading';

const MOBILE_PHASE_PRIORITY = [
  'activity',
  'custom-content',
  'assessment',
  'runtime',
  'default',
];

class Loader extends Component {
  state = {
    mobileVisible: false,
    mobilePhase: null,
    mobilePhaseSource: null,
    mobilePhases: {},
    mobileSpinnerActive: false,
    mobileSpinnerUploading: false,
    mobileCycleId: 0,
  };

  mobileLoadingActive = false;

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.addEventListener(MOBILE_LOADING_PHASE_EVENT, this.handleMobilePhase);
      window.addEventListener(MOBILE_SPINNER_EVENT, this.handleMobileSpinner);
    }

    this.syncMobileVisibility();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.status.isLoading !== this.props.status.isLoading ||
      prevProps.status.uploading !== this.props.status.uploading ||
      prevState.mobilePhases !== this.state.mobilePhases ||
      prevState.mobileSpinnerActive !== this.state.mobileSpinnerActive ||
      prevState.mobileSpinnerUploading !== this.state.mobileSpinnerUploading
    ) {
      this.syncMobileVisibility();
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(MOBILE_LOADING_PHASE_EVENT, this.handleMobilePhase);
      window.removeEventListener(MOBILE_SPINNER_EVENT, this.handleMobileSpinner);
    }

    if (this.mobileHideTimer) {
      window.clearTimeout(this.mobileHideTimer);
      this.mobileHideTimer = null;
    }
  }

  getActivePhase = (phases = this.state.mobilePhases) => {
    for (const source of MOBILE_PHASE_PRIORITY) {
      const phase = phases?.[source];
      if (phase?.active) return { source, phase };
    }

    const fallbackEntry = Object.entries(phases || {})
      .find(([, phase]) => phase?.active);

    return fallbackEntry
      ? { source: fallbackEntry[0], phase: fallbackEntry[1] }
      : null;
  };

  handleMobilePhase = (event) => {
    if (!isMobileAppRuntime()) return;

    const detail = event?.detail || {};
    const source = String(detail.source || 'default');
    const nextPhases = { ...this.state.mobilePhases };

    if (detail.active === false) {
      delete nextPhases[source];
    } else {
      nextPhases[source] = {
        active: true,
        label: detail.label || '',
        detail: detail.detail || '',
        progress: Number.isFinite(detail.progress) ? detail.progress : undefined,
      };
    }

    const selected = this.getActivePhase(nextPhases);

    // Some transitions hand the screen directly from Loader to another full-screen
    // owner (VideoAnimation / a rendered activity). Hide immediately only when the
    // source being cleared was the last active owner. Another source must keep the
    // overlay alive with its own text instead of inheriting the old phase.
    if (detail.active === false && detail.immediate === true) {
      if (this.mobileHideTimer) {
        window.clearTimeout(this.mobileHideTimer);
        this.mobileHideTimer = null;
      }

      const otherLoading = Boolean(
        this.props.status.uploading ||
        this.state.mobileSpinnerActive ||
        selected
      );

      const ownerChanged = Boolean(
        selected?.source
        && this.state.mobilePhaseSource
        && selected.source !== this.state.mobilePhaseSource
      );

      this.mobileLoadingActive = otherLoading;
      this.setState({
        mobilePhases: nextPhases,
        mobilePhase: selected?.phase || null,
        mobilePhaseSource: selected?.source || null,
        mobileVisible: otherLoading,
        mobileCycleId: ownerChanged
          ? this.state.mobileCycleId + 1
          : this.state.mobileCycleId,
      });
      return;
    }

    if (selected) {
      // Progress belongs to a loading owner, not to the overlay globally. If
      // ownership changes (runtime -> assessment -> custom-content -> activity),
      // remount the progress view so a previous owner's 94/100% cannot leak into
      // the next operation and produce impossible states such as
      // `Preparing learning content... 100%` while work is still pending.
      const ownerChanged = Boolean(
        this.state.mobilePhaseSource
        && this.state.mobilePhaseSource !== selected.source
      );

      this.setState({
        mobilePhases: nextPhases,
        mobilePhase: selected.phase,
        mobilePhaseSource: selected.source,
        mobileCycleId: ownerChanged
          ? this.state.mobileCycleId + 1
          : this.state.mobileCycleId,
      });
      return;
    }

    this.setState({
      mobilePhases: nextPhases,
      mobilePhaseSource: null,
      mobilePhase: null,
    });
  };

  handleMobileSpinner = (event) => {
    if (!isMobileAppRuntime()) return;

    const detail = event?.detail || {};
    this.setState({
      mobileSpinnerActive: Boolean(detail.active),
      mobileSpinnerUploading: detail.uploading || false,
    });
  };

  hasMobileLoading = () => {
    const { uploading } = this.props.status;

    // Native mobile uses explicit loading owners / local Spinner signals only.
    // Generic Redux `isLoading` also contains background requests and must never
    // decide whether a full-screen overlay is shown.
    return Boolean(
      uploading ||
      this.state.mobileSpinnerActive ||
      this.getActivePhase()
    );
  };

  syncMobileVisibility = () => {
    if (!isMobileAppRuntime()) return;

    const hasLoading = this.hasMobileLoading();

    if (hasLoading) {
      if (this.mobileHideTimer) {
        window.clearTimeout(this.mobileHideTimer);
        this.mobileHideTimer = null;
      }

      if (!this.mobileLoadingActive) {
        this.mobileLoadingActive = true;
        this.setState({
          mobileVisible: true,
          mobileCycleId: this.state.mobileCycleId + 1,
        });
        return;
      }

      if (!this.state.mobileVisible) {
        this.setState({ mobileVisible: true });
      }
      return;
    }

    this.mobileLoadingActive = false;

    if (!this.state.mobileVisible) return;

    this.setState({
      mobileVisible: false,
      mobilePhase: null,
      mobilePhaseSource: null,
    });
  };

  renderMobileLoader() {
    if (!this.state.mobileVisible) return null;

    const { uploading } = this.props.status;
    const phase = this.state.mobilePhase;
    const effectiveUploading = uploading || this.state.mobileSpinnerUploading;

    return (
      <MobileAppLoadingOverlay
        key={this.state.mobileCycleId}
        uploading={effectiveUploading}
        label={phase?.label}
        detail={phase?.detail}
        phaseSource={this.state.mobilePhaseSource}
        progress={phase?.progress}
      />
    );
  }

  render() {
    const { isLoading, uploading } = this.props.status;

    if (isMobileAppRuntime()) {
      return this.renderMobileLoader();
    }

    return (
      <Fragment>
        {isLoading && <Spinner />}
        {uploading && <Spinner uploading={uploading} />}
      </Fragment>
    );
  }
}

const mapStateToProps = ({ status }) => ({ status });

export default connect(mapStateToProps)(Loader);
